import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Building2, User, CreditCard, CheckCircle, XCircle, Clock, Shield, AlertTriangle, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';

const TABS = [
  { id: 'company', label: 'Company', icon: Building2 },
  { id: 'contact', label: 'Contact', icon: User },
  { id: 'billing', label: 'Billing', icon: CreditCard },
];

export default function AdminClientVerification() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [client, setClient] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('company');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchClient = () => {
    if (!token || !customerId) return;
    api.get(`/customers/${customerId}`)
      .then(r => setClient(r.data))
      .catch(() => toast.error('Failed to load client'))
      .finally(() => setLoading(false));
  };

  const fetchNotes = () => {
    if (!token || !customerId) return;
    api.get(`/clients/${customerId}/notes`)
      .then(r => setNotes(r.data))
      .catch(() => setNotes([]));
  };

  useEffect(() => { fetchClient(); fetchNotes(); }, [token, customerId]);

  const handleVerify = async () => {
    const missing = [];
    if (!client?.company_name) missing.push('Company name');
    if (!client?.email) missing.push('Email');
    if (!client?.phone) missing.push('Phone');
    if (missing.length > 0) {
      // Warn but don't block — admin can verify anyway
      toast.warning(`Note: client is missing ${missing.join(', ')}`);
    }
    setSubmitting(true);
    try {
      await api.put(`/clients/${customerId}/verify`, { status: 'verified' });
      toast.success('Client verified successfully');
      navigate('/admin/customers');
    } catch {
      toast.error('Failed to verify client');
    } finally { setSubmitting(false); }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setSubmitting(true);
    try {
      await api.put(`/clients/${customerId}/verify`, { status: 'rejected' });
      await api.post(`/clients/${customerId}/notes`, { content: 'REJECTED: ' + rejectReason.trim() });
      toast.success('Client rejected');
      setRejectOpen(false);
      navigate('/admin/customers');
    } catch {
      toast.error('Failed to reject client');
    } finally { setSubmitting(false); }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/clients/${customerId}/notes`, { content: newNote.trim() });
      setNewNote('');
      fetchNotes();
      toast.success('Note added');
    } catch {
      toast.error('Failed to add note');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-muted-foreground animate-spin" /></div>;
  if (!client) return (
    <div className="text-center py-12">
      <p className="text-muted-foreground">Client not found</p>
      <Button variant="outline" onClick={() => navigate('/admin/customers')} className="mt-4 border-input text-muted-foreground">Back to Customers</Button>
    </div>
  );

  const checklist = [
    { ok: !!client.company_name, label: 'Company name provided' },
    { ok: !!client.email, label: 'Email provided' },
    { ok: !!client.phone, label: 'Phone provided' },
    { ok: !!client.tax_id, label: 'Tax ID provided' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/admin/customers')} className="text-muted-foreground hover:text-foreground p-2 h-auto">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-4 flex-1">
          <div className="w-14 h-14 rounded-full bg-purple-500/20 flex items-center justify-center text-xl font-bold text-purple-400">
            {client.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Client Verification</h1>
            <p className="text-sm text-muted-foreground">{client.name} {client.company_name ? `· ${client.company_name}` : ''}</p>
          </div>
          <Badge className={`ml-4 text-xs ${
            client.verification_status === 'verified' ? 'bg-emerald-500/20 text-emerald-400' :
            client.verification_status === 'rejected' ? 'bg-red-500/20 text-red-400' :
            'bg-amber-500/20 text-amber-400'
          }`}>
            {client.verification_status === 'verified' ? 'Verified' :
             client.verification_status === 'rejected' ? 'Rejected' : 'Pending'}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tabs */}
          <div className="flex gap-1 bg-muted/50 rounded-lg p-1">
            {TABS.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-xs font-medium transition-colors flex-1 justify-center ${
                    activeTab === tab.id
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <Card className="bg-card border-border">
            <CardContent className="p-5 space-y-4">
              {activeTab === 'company' && (
                <>
                  <p className="text-[10px] text-muted-foreground tracking-wider font-bold">COMPANY INFORMATION</p>
                  <div className="grid grid-cols-2 gap-4">
                    <InfoRow label="Company Name" value={client.company_name} />
                    <InfoRow label="Industry" value={client.industry} />
                    <InfoRow label="Website" value={client.website} />
                    <InfoRow label="Tax ID" value={client.tax_id} />
                  </div>
                  {(client.company_address || client.company_street) && (
                    <div className="border-t border-border pt-4">
                      <p className="text-[10px] text-muted-foreground tracking-wider font-bold mb-3">COMPANY ADDRESS</p>
                      <div className="grid grid-cols-2 gap-4">
                        <InfoRow label="Street" value={client.company_street || client.company_address} />
                        <InfoRow label="City" value={client.company_city} />
                        <InfoRow label="Province" value={client.company_province} />
                        <InfoRow label="Postal Code" value={client.company_postal} />
                      </div>
                    </div>
                  )}
                  {client.company_logo_url && (
                    <div className="border-t border-border pt-4">
                      <p className="text-[10px] text-muted-foreground tracking-wider font-bold mb-3">COMPANY LOGO</p>
                      <img src={client.company_logo_url} alt="Company Logo" className="w-24 h-24 rounded-lg object-contain bg-muted/50 p-2" />
                    </div>
                  )}
                  <div className="border-t border-border pt-4">
                    <InfoRow label="Member Since" value={client.created_at ? new Date(client.created_at).toLocaleDateString() : '—'} />
                  </div>
                </>
              )}

              {activeTab === 'contact' && (
                <>
                  <p className="text-[10px] text-muted-foreground tracking-wider font-bold">CONTACT INFORMATION</p>
                  <div className="grid grid-cols-2 gap-4">
                    <InfoRow label="Primary Contact" value={client.name} />
                    <InfoRow label="Email" value={client.email} />
                    <InfoRow label="Phone" value={client.phone} />
                  </div>
                  <div className="border-t border-border pt-4">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground">Email Status</span>
                      {client.last_login ? (
                        <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px]">
                          <CheckCircle className="w-3 h-3 mr-1" /> Verified (logged in)
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-500/20 text-amber-400 text-[10px]">
                          <Clock className="w-3 h-3 mr-1" /> Unverified
                        </Badge>
                      )}
                    </div>
                  </div>
                </>
              )}

              {activeTab === 'billing' && (
                <>
                  <p className="text-[10px] text-muted-foreground tracking-wider font-bold">BILLING INFORMATION</p>
                  <div className="grid grid-cols-2 gap-4">
                    <InfoRow label="Billing Street" value={client.billing_street || client.billing_address} />
                    <InfoRow label="Billing City" value={client.billing_city} />
                    <InfoRow label="Billing Province" value={client.billing_province} />
                    <InfoRow label="Billing Postal Code" value={client.billing_postal} />
                  </div>
                  <div className="border-t border-border pt-4">
                    <InfoRow label="Payment Terms" value={client.payment_terms || 'Net 30 (default)'} />
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Bottom Actions */}
          {client.verification_status !== 'verified' && (
            <div className="flex gap-3">
              <Button
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-foreground font-bold h-11"
                onClick={handleVerify}
                disabled={submitting}
              >
                <CheckCircle className="w-4 h-4 mr-2" /> Verify Client
              </Button>
              <Button
                variant="outline"
                className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10 font-bold h-11"
                onClick={() => setRejectOpen(true)}
                disabled={submitting}
              >
                <XCircle className="w-4 h-4 mr-2" /> Reject
              </Button>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Verification Checklist */}
          <Card className="bg-card border-border">
            <CardContent className="p-4 space-y-3">
              <p className="text-[10px] text-muted-foreground tracking-wider font-bold flex items-center gap-2">
                <Shield className="w-3.5 h-3.5" /> VERIFICATION CHECKLIST
              </p>
              {checklist.map(ch => (
                <div key={ch.label} className={`text-xs flex items-center gap-2 ${ch.ok ? 'text-emerald-400' : 'text-red-400'}`}>
                  {ch.ok ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                  {ch.label}
                </div>
              ))}
              <div className="border-t border-border pt-3 mt-3">
                <p className="text-[10px] text-muted-foreground">
                  {checklist.filter(c => c.ok).length}/{checklist.length} requirements met
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Admin Notes */}
          <Card className="bg-card border-border">
            <CardContent className="p-4 space-y-4">
              <p className="text-[10px] text-muted-foreground tracking-wider font-bold">ADMIN NOTES</p>
              <div className="space-y-2 max-h-[300px] overflow-auto">
                {notes.length === 0 ? (
                  <p className="text-muted-foreground text-xs py-2">No notes yet</p>
                ) : (
                  notes.map(n => (
                    <div key={n.note_id} className="bg-muted/50 rounded-lg p-2.5">
                      <p className={`text-xs ${n.content?.startsWith('REJECTED:') ? 'text-red-400' : 'text-muted-foreground'}`}>{n.content}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">{n.author_name} · {new Date(n.created_at).toLocaleDateString()}</p>
                    </div>
                  ))
                )}
              </div>
              <Textarea
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                placeholder="Add an internal note..."
                className="bg-muted border-input text-foreground text-xs min-h-[60px]"
              />
              <Button
                size="sm"
                onClick={handleAddNote}
                disabled={!newNote.trim()}
                className="w-full bg-brand-accent text-brand-accent-foreground font-medium text-xs h-8"
              >
                <Send className="w-3.5 h-3.5 mr-1" /> Add Note
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Rejection Dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="bg-surface border-border text-foreground max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" /> Reject Client
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Please provide a reason for rejecting <span className="text-foreground font-medium">{client.name}</span>. This will be recorded as an internal note.
            </p>
            <Textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Reason for rejection (required)..."
              className="bg-muted border-input text-foreground text-xs min-h-[100px]"
            />
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 border-input text-muted-foreground"
                onClick={() => { setRejectOpen(false); setRejectReason(''); }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-foreground font-bold"
                onClick={handleReject}
                disabled={!rejectReason.trim() || submitting}
              >
                <XCircle className="w-4 h-4 mr-2" /> Confirm Rejection
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <p className="text-[10px] text-muted-foreground mb-0.5">{label}</p>
      <p className="text-xs text-foreground">{value || <span className="text-muted-foreground italic">Not provided</span>}</p>
    </div>
  );
}
